/**
 * Usage tracking: what an event IS, before anything writes one.
 *
 * Pure functions only — no DOM, no Firebase, injected clock.
 * (ARCHITECTURE.md §3)
 *
 * ── What the legacy actually does ─────────────────────────────────────────
 * Line numbers READ from `MBM Luxury Prototype/hosting/app.html` (15,903
 * lines) on 16 Aug 2026. The tracker's claim of `8095–8228, 9259–9335` is
 * wrong by more than 7,000 lines: 8095 is the Firestore trips/clients
 * snapshot handler and 9259 is the trip map's pin builder. Neither has
 * anything to do with analytics.
 *
 *   15521–15582  the v1 tracker — `window.mbmTrack`, the login poller, and
 *                the `WRAP` table that wraps UI functions so they emit events
 *   15584–15623  "analytics v2" — session start, JS errors, rage clicks,
 *                field edits, in-app search, and a 3-second page-view poll
 *   15542–15553  the two documents every event writes:
 *                `analytics_events/{autoId}` and `analytics_users/{uid}`
 *
 * `/insights` reads both: `analytics_users.counts` drives its Feature Usage
 * chart, `analytics_events` its activity line and its Concierge panels.
 *
 * ── Bugs found, each with a named regression test ─────────────────────────
 *
 * 1. TWO EVENT TYPES HAVE NEVER BEEN EMITTED, NOT ONCE.
 *    `WRAP` (app.html:15572–15574) names nine functions to wrap, guarded by
 *    `if (typeof window[name] === 'function')` (15577). Two of those names do
 *    not exist anywhere in app.html: `generateSocialGraphic` and
 *    `archiveTrip`. The social export is `openSocialMediaExport`
 *    (app.html:9391) and there is no archive FUNCTION at all — archiving sets
 *    `t.archived` in place. So `social_export` and `trip_archived` are
 *    columns that can never appear on the admin dashboard's Feature Usage
 *    chart, and the absence reads as "nobody uses this feature" rather than
 *    as "this was never wired". It is the §5b `allTrips` bug wearing a
 *    disguise, and `scripts/check.mjs`'s phantom-guard scanner cannot see it
 *    because `typeof window[name]` is a COMPUTED member access, not a bare
 *    identifier. `TRACKED_ACTIONS` below names only functions that exist, and
 *    `PHANTOM_ACTIONS` records the two that did not so the mistake cannot be
 *    re-introduced silently.
 *
 * 2. AN EVENT TYPE CONTAINING A DOT SILENTLY BECOMES A NESTED FIELD.
 *    app.html:15548 builds the rollup as `counts[type] = increment(1)` and
 *    hands the object to `setDoc(..., {merge:true})`. Firestore reads a plain
 *    object key literally, but `mbmTrack` is `window.`-public and takes any
 *    string; a type of `trip.created` written through a field PATH would nest,
 *    and a type containing `/` or `__` is rejected outright by the SDK — which
 *    throws inside the `.then()` and is swallowed by the `.catch(function(){})`
 *    at 15554. The event is lost with no sign. `isTrackableType` rejects those
 *    shapes up front so the caller learns.
 *
 * 3. `meta` IS WRITTEN UNBOUNDED AND UNFILTERED.
 *    15551 stores `meta || {}` as given. `js_error` (15596) puts up to 200
 *    characters of an error message in there, and a message can quote a
 *    client's name or email. The v2 comment promises "field edits log the
 *    field NAME only, never the typed value" — true of `field_edit` and of
 *    nothing else. `sanitizeMeta` caps the size and drops anything that looks
 *    like an address, so the promise holds for every event rather than one.
 */

/** Firestore's own limit; a doc rejected for size is an event silently lost. */
export const MAX_META_KEYS = 12

/** Characters past this are noise on a dashboard and weight in a document. */
export const MAX_META_LENGTH = 200

/**
 * UI functions that emit an event when called, and the event each emits.
 *
 * Every name here is a function that EXISTS in `app.html` — checked, not
 * assumed. See bug 1: the legacy table listed two that never did.
 */
export const TRACKED_ACTIONS = Object.freeze({
  createTrip: 'trip_created',
  deleteTrip: 'trip_deleted',
  openTrip: 'trip_opened',
  duplicateTrip: 'trip_duplicated',
  openConcierge: 'concierge_opened',
  openSocialMediaExport: 'social_export',
  saveBrandSettings: 'branding_saved',
  printTrip: 'print_trip',
})

/**
 * The two names `app.html:15572–15574` wrapped that were never defined, and
 * what each should have been. Kept as data so the regression test can name
 * them and so nobody re-adds one from an old copy of the table.
 */
export const PHANTOM_ACTIONS = Object.freeze({
  generateSocialGraphic: 'openSocialMediaExport',
  archiveTrip: '', // there is no archive function; `t.archived` is set in place
})

/**
 * Every event type the product emits, with a plain-English description.
 *
 * This is the list the Analytics screen prints. An advisor is entitled to
 * read what is recorded about them, and a list assembled by hand somewhere
 * else would drift from the code the first time an event was added.
 */
export const EVENT_CATALOG = Object.freeze([
  { type: 'login', what: 'You signed in. Recorded once per session.' },
  { type: 'session_start', what: 'A session began, with your window size and the page you came from.' },
  { type: 'page_view', what: 'Which of the three main screens you moved to.' },
  { type: 'trip_created', what: 'A trip was created.' },
  { type: 'trip_opened', what: 'A trip was opened.' },
  { type: 'trip_duplicated', what: 'A trip was duplicated.' },
  { type: 'trip_deleted', what: 'A trip was deleted.' },
  { type: 'print_trip', what: 'A trip was printed or exported to PDF.' },
  { type: 'social_export', what: 'A social graphic was exported.' },
  { type: 'branding_saved', what: 'Branding settings were saved.' },
  { type: 'concierge_opened', what: 'The Concierge desk was opened.' },
  { type: 'concierge_message', what: 'A message was sent to the Concierge. The text is never recorded.' },
  { type: 'concierge_feedback', what: 'You rated a Concierge answer up or down.' },
  { type: 'spreadsheet_import', what: 'Trips were imported from a spreadsheet, and how many.' },
  { type: 'field_edit', what: 'Which field you edited. The value you typed is never recorded.' },
  { type: 'search', what: 'That you searched. The search text is never recorded.' },
  { type: 'rage_click', what: 'Four clicks in one spot — a control that looks broken.' },
  { type: 'js_error', what: 'An error message, so a crash can be fixed.' },
  { type: 'sheet_sync', what: 'Trips were synced to your webhook, and how many failed.' },
  { type: 'csv_export', what: 'A CSV was downloaded, and how many trips it held.' },
])

/**
 * Is this a type Firestore can hold as a plain `counts` key?
 *
 * Rejects the empty string, anything with `.` `/` `[` `]` `*` or backtick,
 * a leading/trailing `__`, and anything absurdly long. See bug 2 — every one
 * of these either nests silently or throws inside a swallowed promise.
 *
 * @param {unknown} type
 * @returns {boolean}
 */
export function isTrackableType(type) {
  if (typeof type !== 'string') return false
  const t = type.trim()
  if (!t || t.length > 64) return false
  if (t !== type) return false
  if (/[./[\]*`~]/.test(t)) return false
  if (t.startsWith('__') && t.endsWith('__')) return false
  return true
}

/**
 * Who the actor is. Port of legacy `getAgentIdentity` (app.html:8258–8268).
 *
 * Legacy reads the ambient `currentUser` global inside a `try`; this takes
 * the user as an argument, which is the whole difference between a function
 * that can be tested and one that cannot.
 *
 * @param {{displayName?: string, email?: string, uid?: string}|null|undefined} user
 * @returns {{uid: string, firstName: string, lastName: string, email: string, name: string}}
 */
export function agentIdentity(user) {
  const u = user || {}
  const display = String(u.displayName || '').trim()
  const parts = display ? display.split(/\s+/) : []
  const firstName = parts.shift() || ''
  const lastName = parts.join(' ')
  const email = String(u.email || '').trim()
  return {
    uid: String(u.uid || ''),
    firstName,
    lastName,
    email,
    /* Legacy falls back to the whole email address when there is no display
       name (app.html:8305). Kept: a blank actor on a usage report is worse
       than an address the advisor already gave us. */
    name: display || email,
  }
}

/**
 * Trim `meta` to something safe to store.
 *
 * Values are coerced to string, number or boolean — an object nested inside
 * `meta` is a second unbounded document. Anything that looks like an email
 * address is replaced, because bug 3 is that `js_error` can quote one and
 * nothing was stopping it.
 *
 * @param {Record<string, any>|null|undefined} meta
 * @returns {Record<string, string|number|boolean>}
 */
export function sanitizeMeta(meta) {
  /** @type {Record<string, string|number|boolean>} */
  const out = {}
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return out

  let kept = 0
  for (const [key, value] of Object.entries(meta)) {
    if (kept >= MAX_META_KEYS) break
    if (!isTrackableType(key)) continue
    if (value == null) continue

    if (typeof value === 'boolean') {
      out[key] = value
    } else if (typeof value === 'number') {
      out[key] = Number.isFinite(value) ? value : 0
    } else {
      const text = String(value).replace(/[\w.+-]+@[\w.-]+\.\w+/g, '[email]')
      out[key] = text.slice(0, MAX_META_LENGTH)
    }
    kept++
  }
  return out
}

/**
 * The `analytics_events/{autoId}` document. Legacy app.html:15543–15552.
 *
 * `day` is UTC, matching both the legacy writer (15551) and the buckets
 * `/insights` builds to read it back (insights.html:317) — the two agree, so
 * this stays UTC rather than becoming the third answer.
 *
 * @param {{type: string, meta?: Record<string, any>, user?: any, agency?: string, nowMs?: number}} input
 * @returns {{uid: string, email: string, name: string, agency: string, type: string, meta: Record<string, any>, day: string}}
 */
export function usageEvent(input) {
  const { type, meta, user, agency, nowMs } = input || {}
  const me = agentIdentity(user)
  const at = new Date(Number.isFinite(nowMs) ? Number(nowMs) : 0)
  return {
    uid: me.uid,
    email: me.email,
    name: String((user || {}).displayName || '').trim(),
    agency: String(agency || '').trim(),
    type: String(type || ''),
    meta: sanitizeMeta(meta),
    day: at.toISOString().slice(0, 10),
  }
}

/**
 * The `analytics_users/{uid}` rollup, minus the two server-generated values
 * (`lastSeen` and the `counts` increment) which only `index.js` can build.
 * Legacy app.html:15554–15557.
 *
 * @param {{user?: any, agency?: string}} input
 * @returns {{uid: string, email: string, name: string, agency: string}}
 */
export function usageRollup(input) {
  const { user, agency } = input || {}
  const me = agentIdentity(user)
  return {
    uid: me.uid,
    email: me.email,
    name: String((user || {}).displayName || '').trim(),
    agency: String(agency || '').trim(),
  }
}
